import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Paper, Alert, CircularProgress, MenuItem, Select, FormControl, InputLabel, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import axios from 'axios';
import config from '../config';
import DeleteIcon from '@mui/icons-material/Delete';

const Upload = () => {
    const [file, setFile] = useState(null);
    const [tableName, setTableName] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [overwrite, setOverwrite] = useState(false);

    // Delete feature states
    const [tables, setTables] = useState([]);
    const [selectedTableToDelete, setSelectedTableToDelete] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        fetchTables();
    }, []);

    const fetchTables = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/fournisseurs`);
            setTables(response.data);
        } catch (err) {
            console.error('Failed to fetch tables', err);
        }
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setError(null);
        setMessage(null);
    };

    const handleTableNameChange = (e) => {
        setTableName(e.target.value);
        setError(null);
        setMessage(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file || !tableName) {
            setError('Please select a file and provide a table name.');
            return;
        }

        // Validation: Table name must start with 'fournisseurs_'
        if (!tableName.startsWith('fournisseurs_')) {
            setError('Table name must start with "fournisseurs_"');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('tableName', tableName);
        formData.append('overwrite', overwrite);

        setLoading(true);
        setError(null);
        setMessage(null);

        try {
            await axios.post(`${config.API_BASE_URL}/fournisseurs/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setMessage(`Table "${tableName}" processed successfully!`);
            setFile(null);
            setTableName('');
            setOverwrite(false);
            fetchTables(); // Refresh list
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'An error occurred during upload.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = () => {
        if (selectedTableToDelete) {
            setDeleteDialogOpen(true);
        }
    };

    const handleConfirmDelete = async () => {
        setDeleteLoading(true);
        try {
            await axios.delete(`${config.API_BASE_URL}/fournisseurs/${selectedTableToDelete}`);
            setMessage(`Table "${selectedTableToDelete}" deleted successfully.`);
            setSelectedTableToDelete('');
            fetchTables(); // Refresh list
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || 'Failed to delete table.');
        } finally {
            setDeleteLoading(false);
            setDeleteDialogOpen(false);
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Manage Supplier Data
            </Typography>

            {/* Upload Section */}
            <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
                <Typography variant="h6" gutterBottom>Upload New Table</Typography>
                {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <form onSubmit={handleSubmit}>
                    <TextField
                        label="Table Name (must start with 'fournisseurs_')"
                        fullWidth
                        margin="normal"
                        value={tableName}
                        onChange={handleTableNameChange}
                        required
                        helperText="Example: fournisseurs_2025_v1"
                    />

                    <Box sx={{ mt: 2, mb: 2 }}>
                        <input
                            accept=".xlsx, .xls"
                            style={{ display: 'none' }}
                            id="raised-button-file"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="raised-button-file">
                            <Button variant="outlined" component="span" fullWidth>
                                {file ? file.name : 'Select Excel File'}
                            </Button>
                        </label>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <input
                            type="checkbox"
                            id="overwrite-checkbox"
                            checked={overwrite}
                            onChange={(e) => setOverwrite(e.target.checked)}
                            style={{ marginRight: '8px' }}
                        />
                        <Typography variant="body2" component="label" htmlFor="overwrite-checkbox">
                            Ecraser si la table existe déjà (Overwrite)
                        </Typography>
                    </Box>

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={loading}
                        sx={{ mt: 2 }}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Upload'}
                    </Button>
                </form>
            </Paper>

            {/* Delete Section */}
            <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
                <Typography variant="h6" gutterBottom color="error">Delete Existing Table</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Select Table to Delete</InputLabel>
                        <Select
                            value={selectedTableToDelete}
                            label="Select Table to Delete"
                            onChange={(e) => setSelectedTableToDelete(e.target.value)}
                        >
                            {tables.map((table) => {
                                const tableName = typeof table === 'string' ? table : table.name;
                                return (
                                    <MenuItem key={tableName} value={tableName}>
                                        {tableName}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteClick}
                        disabled={!selectedTableToDelete}
                        startIcon={<DeleteIcon />}
                    >
                        Delete
                    </Button>
                </Box>
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the table "{selectedTableToDelete}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" autoFocus disabled={deleteLoading}>
                        {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Upload;
